import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Plus, Pencil, Trash2, ArrowUpRight, Archive, FileText, Calculator, Camera, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import { Modal, Field, Grid2, Input, Select, Th } from "@/pages/Contacts";
import { StatusPill } from "@/pages/Dashboard";
import { ExportButtons, ImportButton } from "@/components/ExportImport";
import ConfirmDialog from "@/components/ConfirmDialog";
import GrammarCheck from "@/components/GrammarCheck";
import MiniPipeline from "@/components/MiniPipeline";
import { useAuth } from "@/context/AuthContext";


/** Live preview chip that shows which spec-sheet template will render
    for the current/proposed roof type combo. */
export function ScopePreview({ currentRoof, proposedRoof }) {
  const [preview, setPreview] = useState(null);
  const proposed = proposedRoof || "";
  useEffect(() => {
    if (!proposed) return undefined;
    let cancelled = false;
    const handle = async () => {
      try {
        const r = await api.get("/options/scope-preview", {
          params: { proposed, current: currentRoof || "" },
        });
        if (cancelled) return;
        setPreview(r.data);
      } catch (e) {
        if (cancelled) return;
        setPreview(null);
      }
    };
    const t = setTimeout(handle, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [currentRoof, proposed]);

  if (!proposed || !preview?.title) return null;
  const newCx = preview.is_new_construction;
  return (
    <div
      className={`mt-2 inline-flex items-start gap-2 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm border ${
        newCx ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-blue-50 text-blue-800 border-blue-200"
      }`}
      data-testid="scope-preview-chip"
    >
      <FileText className="w-3 h-3 mt-px flex-shrink-0" />
      <div className="leading-snug">
        <div>Will generate:</div>
        <div className="font-black tracking-[0.05em] mt-0.5">{preview.title}</div>
      </div>
    </div>
  );
}


/** Default exclusions for Construction Project / Other scopes. These are policy-level
    boilerplate that rarely changes from project to project. New deals start with this
    pre-filled; users can edit or wipe it but it's also re-applied on the PDF if the
    field happens to be blank. */
export const DEFAULT_CONSTRUCTION_EXCLUSIONS = [
  "Permit fees (if required by jurisdiction).",
  "Removal/disposal of pre-existing hazardous materials.",
  "Work outside the defined scope.",
].join("\n");

/** The four scope "work types" — one selector drives which systems, pricing and
    scope fields show. Construction lives here too so remodels/repairs are built
    in the exact same place as roofing scopes. */
export const WORK_TYPES = [
  { key: "fluid", label: "Fluid Applied Roofing", desc: "FARM · Silicone · Acrylic coating systems", default: "FARM (Fluid Applied Reinforced Membrane)" },
  { key: "traditional", label: "Traditional Commercial", desc: "TPO · EPDM · PVC · ModBit · BUR · Metal · Tile", default: "TPO Over-Lay" },
  { key: "shingle", label: "Shingle Roofing", desc: "Asphalt shingle systems", default: "Shingle" },
  { key: "construction", label: "Construction / Remodel", desc: "Repairs, remodels & non-roofing work", default: "Construction Project" },
];

export const workCategoryOf = (rt = "") => {
  if (/^(construction project|other)$/i.test(rt)) return "construction";
  if (/shingle/i.test(rt)) return "shingle";
  if (/farm|fluid applied|silicone|acrylic/i.test(rt)) return "fluid";
  return "traditional";
};

/** Days a Lead-status scope has sat unsent (0 when fresh or already sent). */
export const staleScopeDays = (d) => {
  if ((d.status || "") !== "Lead") return 0;
  if (d.last_scope_sent_at) return 0;
  const t = Date.parse(d.created_at || "");
  if (!t) return 0;
  const days = Math.floor((Date.now() - t) / 86400000);
  return days > 5 ? days : 0;
};

const daysAgo = (iso) => {
  const t = Date.parse(iso || "");
  return t ? Math.max(0, Math.floor((Date.now() - t) / 86400000)) : null;
};

/** Sign-Off Tracking chip — Signed / Opened / Sent-not-opened for emailed scopes. */
export const SignOffChip = ({ d }) => {
  const status = d.status || "";
  if (d.scope_signed_at && !/lost|past/i.test(status)) {
    return (
      <span
        className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm bg-emerald-100 text-emerald-800 align-middle"
        title={`Customer signed off on ${(d.scope_signed_at || "").slice(0, 10)}.`}
        data-testid={`signoff-signed-${d.id}`}
      >
        Signed ✓
      </span>
    );
  }
  if (!d.last_scope_sent_at || !/^(lead|sent)$/i.test(status)) return null;
  if (d.proposal_first_viewed_at) {
    const ago = daysAgo(d.proposal_last_viewed_at || d.proposal_first_viewed_at);
    const views = d.proposal_view_count || 1;
    return (
      <span
        className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm bg-sky-100 text-sky-800 align-middle"
        title={`Customer opened the proposal ${views} time${views === 1 ? "" : "s"}, last ${ago === 0 ? "today" : `${ago}d ago`} — good time to call.`}
        data-testid={`signoff-opened-${d.id}`}
      >
        Opened · {ago === 0 ? "today" : `${ago}d ago`}
      </span>
    );
  }
  const sentAgo = daysAgo(d.last_scope_sent_at);
  return (
    <span
      className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm bg-zinc-200 text-zinc-600 align-middle"
      title={`Emailed ${sentAgo === 0 ? "today" : `${sentAgo}d ago`} but the sign-off link hasn't been opened yet — worth a follow-up call.`}
      data-testid={`signoff-unopened-${d.id}`}
    >
      Sent · not opened
    </span>
  );
};

/** Common shingle product lines per grade — datalist suggestions only. */
export const SHINGLE_PRODUCTS = {
  designer: ["GAF Grand Sequoia", "GAF Camelot II", "GAF Woodland", "Owens Corning Berkshire", "CertainTeed Grand Manor", "CertainTeed Carriage House", "Malarkey Windsor"],
  architectural: ["GAF Timberline HDZ", "GAF Timberline UHDZ", "Owens Corning Duration", "Owens Corning TruDefinition Duration", "CertainTeed Landmark", "CertainTeed Landmark Pro", "Malarkey Vista", "IKO Dynasty"],
  three_tab: ["GAF Royal Sovereign", "Owens Corning Supreme", "CertainTeed XT 25", "IKO Marathon"],
};


const empty = {
  title: "",
  deal_type: "Scope",
  contact_id: "",
  customer_contact_id: "",
  owner_contact_id: "",
  property_id: "",
  assigned_to_user_id: "",
  lead_source: "Personal",
  referral_source: "",
  project_type: "Repair",
  current_roof_type: "None (new construction)",
  proposed_roof_type: "TPO Over-Lay",
  custom_scope: "",
  construction_project_requirements: "",
  construction_other_requirements: "",
  construction_exclusions: DEFAULT_CONSTRUCTION_EXCLUSIONS,
  construction_scope_subtitle: "",
  project_type_override: "",
  property_sqft: 0,
  perimeter_lnft: 0,
  avg_parapet_height: 0,
  total_sqft: 0,
  proposal_option_1: 0,
  proposal_option_2: 0,
  proposal_option_3: 0,
  proposal_option_25yr: 0,
  proposal_option_1_label: "",
  proposal_option_2_label: "",
  proposal_option_3_label: "",
  proposal_option_25yr_label: "",
  chosen_amount: 0,
  chosen_date: "",
  date_sent: "",
  status: "Lead",
  lost_reason: "",
  lost_reason_detail: "",
  materials_cost: 0,
  labor_cost: 0,
  subcontractor_cost: 0,
  other_expenses: 0,
  payment_milestones: [],
  cost_items: [],
  notes: "",
  product_description: "",
  warranty_20yr_add: 0,
  warranty_15yr_add: 0,
  warranty_10yr_add: 0,
  warranty_25yr_add: 0,
  warranty_color: "white",
  cover_photo_file_id: "",
  scheduled_start_date: "",
  scheduled_end_date: "",
  material_order_date: "",
};

export default function Deals() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [dealInvoicesMap, setDealInvoicesMap] = useState({});
  const [photoCounts, setPhotoCounts] = useState({});
  const [users, setUsers] = useState([]);
  const [options, setOptions] = useState({ lead_sources: [], project_types: [], roof_types: [], current_roof_types: [], deal_statuses: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");
  // Read `?filter=<status>` from the URL so Dashboard KPI cards can deep-link
  // into a pre-filtered view (Open Leads → Lead, Won Deals → Won). Runs on
  // mount and whenever the query string changes.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const q = searchParams.get("filter");
    if (q) setFilter(q);
  }, [searchParams]);
  const [stageFilter, setStageFilter] = useState("all");
  // `?edit=<dealId>` deep-link — lets DealDetail's "Edit Scope Data" jump
  // straight into the edit modal for construction/remodel deals.
  const editParamHandled = useRef(false);
  useEffect(() => {
    if (editParamHandled.current) return;
    const editId = searchParams.get("edit");
    if (editId && items.length) {
      const d = items.find((x) => x.id === editId);
      if (d) { editParamHandled.current = true; openEdit(d); }
    }
  }, [items, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [ctTemplates, setCtTemplates] = useState([]);
  const loadCtTemplates = () =>
    api.get("/construction-scope-templates").then((r) => setCtTemplates(r.data || [])).catch(() => {});

  const applyCtTemplate = (t) => {
    setForm((f) => ({
      ...f,
      project_type_override: t.project_type_override || f.project_type_override,
      construction_scope_subtitle: t.scope_subtitle || f.construction_scope_subtitle,
      construction_project_requirements: t.project_requirements || "",
      construction_other_requirements: t.other_requirements || "",
      construction_exclusions: (t.exclusions || "").trim() || DEFAULT_CONSTRUCTION_EXCLUSIONS,
    }));
    toast.success(`Template "${t.name}" applied — edit any field before saving`);
  };

  const saveCtTemplate = async () => {
    if (!(form.construction_project_requirements || "").trim()) {
      toast.error("Fill in Project Requirements first — that's the heart of the template.");
      return;
    }
    const name = window.prompt("Template name (e.g., Drainage & Grading):", form.project_type_override || "");
    if (!name || !name.trim()) return;
    try {
      await api.post("/construction-scope-templates", {
        name: name.trim(),
        project_type_override: form.project_type_override || "",
        scope_subtitle: form.construction_scope_subtitle || "",
        project_requirements: form.construction_project_requirements || "",
        other_requirements: form.construction_other_requirements || "",
        exclusions: form.construction_exclusions || "",
      });
      toast.success(`Template "${name.trim()}" saved — reusable on any construction scope`);
      loadCtTemplates();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const deleteCtTemplate = async (t) => {
    try {
      await api.delete(`/construction-scope-templates/${t.id}`);
      toast.success(`Template "${t.name}" deleted`);
      loadCtTemplates();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const load = () => api.get("/deals").then((r) => setItems(r.data));

  useEffect(() => {
    load();
    api.get("/contacts").then((r) => setContacts(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/invoices").then((r) => {
      const map = {};
      for (const inv of (r.data || [])) {
        if (!inv.deal_id) continue;
        (map[inv.deal_id] ||= []).push(inv);
      }
      setDealInvoicesMap(map);
    }).catch(() => setDealInvoicesMap({}));
    api.get("/deals/photo-counts").then((r) => setPhotoCounts(r.data || {})).catch(() => setPhotoCounts({}));
    api.get("/options").then((r) => setOptions(r.data));
    loadCtTemplates();
  }, []);

  // `?new=<worktype>` deep-link — Build Scope lane switcher opens the create
  // modal with the right work type preselected (shingle / construction).
  const newParamHandled = useRef(false);
  useEffect(() => {
    if (newParamHandled.current) return;
    const nw = searchParams.get("new");
    if (!nw) return;
    newParamHandled.current = true;
    const wt = WORK_TYPES.find((w) => w.key === nw);
    setEditing(null);
    setForm({ ...empty, ...(wt ? { proposed_roof_type: wt.default } : {}) });
    setOpen(true);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const [sendingProposalId, setSendingProposalId] = useState(null);
  const sendProposal = async (d) => {
    if (!window.confirm(`Email the scope PDF for "${d.title}" to the project's primary contact and mark it Sent?`)) return;
    setSendingProposalId(d.id);
    try {
      const r = await api.post(`/deals/${d.id}/spec-sheet/email`, { mark_sent: true });
      toast.success(r.data?.message || "Proposal sent");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSendingProposalId(null);
    }
  };

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      ...empty,
      ...d,
      contact_id: d.contact_id || "",
      customer_contact_id: d.customer_contact_id || "",
      owner_contact_id: d.owner_contact_id || "",
      property_id: d.property_id || "",
      // Pre-fill exclusions defaults on legacy deals that never set their own
      construction_exclusions: (d.construction_exclusions || "").trim() || DEFAULT_CONSTRUCTION_EXCLUSIONS,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.contact_id) payload.contact_id = null;
      if (!payload.customer_contact_id) payload.customer_contact_id = null;
      if (!payload.owner_contact_id) payload.owner_contact_id = null;
      if (!payload.property_id) payload.property_id = null;
      if (editing) {
        await api.put(`/deals/${editing.id}`, payload);
        toast.success("Deal updated");
      } else {
        await api.post(`/deals`, payload);
        toast.success("Deal created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const remove = (d) => setConfirmTarget(d);

  const removeConfirmed = async () => {
    if (!confirmTarget) return;
    try {
      await api.delete(`/deals/${confirmTarget.id}`);
      toast.success("Project deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setConfirmTarget(null);
    }
  };

  const archive = (deal) => setArchiveTarget(deal);

  const archiveConfirmed = async () => {
    if (!archiveTarget) return;
    try {
      await api.put(`/deals/${archiveTarget.id}`, { ...archiveTarget, status: "Past Lead" });
      toast.success("Moved to Past Lead Prospects");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setArchiveTarget(null);
    }
  };

  const contactOpts = [{ value: "", label: "— None —" }, ...contacts.map((c) => ({ value: c.id, label: `${c.contact_name}${c.company_name ? " · " + c.company_name : ""}` }))];
  const propertyOpts = [{ value: "", label: "— None —" }, ...properties.map((p) => ({ value: p.id, label: p.property_name }))];
  const userOpts = [{ value: "", label: "— Unassigned —" }, ...users.map((u) => ({ value: u.id, label: `${u.name}${u.role !== "admin" ? " · " + u.role : ""}` }))];

  // Hide Past Lead from default filter chips; show via dedicated toggle in "All"
  const VISIBLE_FILTERS = ["All", "Lead", "Sent", "Won", "Lost", "Past Lead", "Archived"];
  const FILTERS = VISIBLE_FILTERS.filter((f) => f === "All" || f === "Archived" || (options.deal_statuses || []).includes(f));
  // "Won" filter also matches "Complete" — Complete is the terminal state
  // set by Mark Complete + Close-Out and is semantically a won deal. This
  // keeps the Dashboard KPI card (/projects?filter=Won) accurate after a
  // deal is fully closed.
  // Archived deals (status starts with "Archived", e.g. closed-out jobs and
  // completed maintenance imports) are hidden everywhere except the Archived chip.
  const isArchived = (d) => String(d.status || "").startsWith("Archived");
  const baseFiltered = filter === "All"
    ? items.filter((d) => d.status !== "Past Lead" && !isArchived(d))
    : filter === "Won"
      ? items.filter((d) => d.status === "Won" || d.status === "Complete")
      : filter === "Archived"
        ? items.filter(isArchived)
        : items.filter((d) => d.status === filter);

  // Pipeline-stage filter — overlay on top of status filter
  const STAGE_OPTS = [
    { key: "all", label: "All Stages" },
    { key: "assessment", label: "Assessment" },
    { key: "scope", label: "Scope Sent" },
    { key: "won", label: "Won/Signed" },
    { key: "deposit", label: "Deposit Paid" },
    { key: "materials", label: "Materials" },
    { key: "scheduled", label: "Scheduled" },
    { key: "in_progress", label: "In Progress" },
    { key: "final_inspection", label: "Final Inspection" },
    { key: "closed", label: "Closed" },
  ];
  const filtered = stageFilter === "all"
    ? baseFiltered
    : baseFiltered.filter((d) => {
        const inv = dealInvoicesMap[d.id] || [];
        const ms = d.payment_milestones || [];
        const status = (d.status || "").toLowerCase();
        const today = new Date().toISOString().slice(0, 10);
        const stages = {
          assessment: !!d.last_scope_sent_at || !!d.last_assessment_id,
          scope: !!d.last_scope_sent_at || !!d.custom_scope || !!d.scope_signed_at,
          won: ["won","in progress","complete","closed"].includes(status),
          deposit: (ms[0] && ms[0].status === "Paid") || inv.some((i) => (i.status||"").toLowerCase()==="paid"),
          materials: !!d.material_order_date,
          scheduled: !!d.scheduled_start_date,
          in_progress: status === "in progress" || (!!d.scheduled_start_date && d.scheduled_start_date <= today),
          final_inspection: (!!d.scheduled_end_date && d.scheduled_end_date <= today) || ["complete","closed"].includes(status),
          closed: ["complete","closed"].includes(status) || (ms.length>0 && ms.every((m)=>m.status==="Paid")),
        };
        // Determine current stage (lastTrue + 1) — match deals whose CURRENT stage equals this filter
        const order = ["assessment","scope","won","deposit","materials","scheduled","in_progress","final_inspection","closed"];
        let lastTrue = -1;
        order.forEach((s, i) => { if (stages[s]) lastTrue = i; });
        const currentKey = stages[stageFilter] && lastTrue === order.indexOf(stageFilter)
          ? stageFilter
          : order[Math.min(order.length - 1, lastTrue + 1)];
        return currentKey === stageFilter;
      });

  return (
    <div className="p-6 sm:p-8 animate-in fade-in duration-500" data-testid="deals-page">
      <div className="flex items-end justify-between mb-8 pb-6 border-b border-zinc-200 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700 mb-2">Pipeline</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Projects</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButtons category="projects" />
          <ImportButton category="projects" onImported={load} />
          <button
            data-testid="new-deal-button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 h-10 text-xs font-bold uppercase tracking-wider hover:bg-blue-800 rounded-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> New Project
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setFilter(f)}
            className={`px-3 h-8 text-[10px] font-bold uppercase tracking-wider border rounded-sm transition-colors ${
              filter === f ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950"
            }`}
          >
            {f === "Past Lead" ? "Past Leads" : f}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-6" data-testid="stage-filters">
        <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mr-1">Stage:</span>
        {STAGE_OPTS.map((s) => {
          const count = s.key === "all"
            ? baseFiltered.length
            : baseFiltered.filter((d) => {
                const inv = dealInvoicesMap[d.id] || [];
                const ms = d.payment_milestones || [];
                const status = (d.status || "").toLowerCase();
                const today = new Date().toISOString().slice(0, 10);
                const stages = {
                  assessment: !!d.last_scope_sent_at || !!d.last_assessment_id,
                  scope: !!d.last_scope_sent_at || !!d.custom_scope || !!d.scope_signed_at,
                  won: ["won","in progress","complete","closed"].includes(status),
                  deposit: (ms[0] && ms[0].status === "Paid") || inv.some((i) => (i.status||"").toLowerCase()==="paid"),
                  materials: !!d.material_order_date,
                  scheduled: !!d.scheduled_start_date,
                  in_progress: status === "in progress" || (!!d.scheduled_start_date && d.scheduled_start_date <= today),
                  final_inspection: (!!d.scheduled_end_date && d.scheduled_end_date <= today) || ["complete","closed"].includes(status),
                  closed: ["complete","closed"].includes(status) || (ms.length>0 && ms.every((m)=>m.status==="Paid")),
                };
                const order = ["assessment","scope","won","deposit","materials","scheduled","in_progress","final_inspection","closed"];
                let lastTrue = -1;
                order.forEach((x, i) => { if (stages[x]) lastTrue = i; });
                const currentKey = stages[s.key] && lastTrue === order.indexOf(s.key)
                  ? s.key
                  : order[Math.min(order.length - 1, lastTrue + 1)];
                return currentKey === s.key;
              }).length;
          return (
            <button
              key={s.key}
              onClick={() => setStageFilter(s.key)}
              className={`inline-flex items-center gap-1.5 px-2.5 h-7 text-[10px] font-bold uppercase tracking-wider border rounded-sm transition-colors ${
                stageFilter === s.key ? "bg-blue-700 text-white border-blue-700" : "bg-white text-zinc-700 border-zinc-300 hover:border-blue-700"
              }`}
              data-testid={`stage-filter-${s.key}`}
            >
              {s.label}
              <span className={`text-[9px] font-mono px-1 rounded-sm ${stageFilter === s.key ? "bg-blue-900" : "bg-zinc-100"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500">No projects match.</div>
        ) : (
          <table className="w-full text-sm" data-testid="deals-table">
            <thead>
              <tr className="border-b-2 border-zinc-950 text-left">
                <Th>Title</Th><Th>Type</Th><Th>Status</Th><Th>Pipeline</Th><Th>Lead Source</Th><Th>Project</Th><Th>Current → Proposed</Th><Th>Chosen</Th><Th>Profit</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const costs = (d.materials_cost || 0) + (d.labor_cost || 0) + (d.subcontractor_cost || 0) + (d.other_expenses || 0);
                const profit = (d.chosen_amount || 0) - costs;
                const isOpen = ["Lead", "Sent"].includes(d.status);
                const propOpts = [d.proposal_option_1, d.proposal_option_2, d.proposal_option_3, d.proposal_option_25yr]
                  .map((v) => Number(v) || 0).filter((v) => v > 0).sort((a, b) => a - b);
                const estValue = propOpts.length ? propOpts[Math.floor(propOpts.length / 2)] : 0;
                // Estimated profit for open deals with no actual costs yet:
                // 33% of the deal value (13% overhead + 20% profit).
                const profitBasis = d.chosen_amount > 0 ? d.chosen_amount : estValue;
                const showEstProfit = isOpen && costs === 0 && profitBasis > 0;
                const estProfit = profitBasis * 0.33;
                return (
                  <tr key={d.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`deal-row-${d.id}`}>
                    <td className="px-6 py-3">
                      <Link to={`/projects/${d.id}`} className="font-bold text-zinc-950 hover:text-blue-700 inline-flex items-center gap-1">
                        {d.title} <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                      {staleScopeDays(d) > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 align-middle">
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm bg-amber-100 text-amber-800"
                            title={`Scope created ${staleScopeDays(d)} days ago and still not sent — send the proposal or follow up.`}
                            data-testid={`stale-scope-badge-${d.id}`}
                          >
                            <AlertTriangle className="w-2.5 h-2.5" />
                            Not sent · {staleScopeDays(d)}d
                          </span>
                          <button
                            type="button"
                            data-testid={`send-proposal-${d.id}`}
                            disabled={sendingProposalId === d.id}
                            onClick={() => sendProposal(d)}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm border border-amber-700 text-amber-800 bg-white hover:bg-amber-50 disabled:opacity-50"
                            title="One-click: email the scope PDF to the primary contact and mark this deal Sent."
                          >
                            <Send className="w-2.5 h-2.5" />
                            {sendingProposalId === d.id ? "Sending…" : "Send Proposal"}
                          </button>
                        </span>
                      )}
                      <SignOffChip d={d} />
                      {photoCounts[d.id] > 0 && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm bg-sky-100 text-sky-800 align-middle"
                          title={`${photoCounts[d.id]} photo${photoCounts[d.id] === 1 ? "" : "s"} in this project`}
                          data-testid={`photo-count-badge-${d.id}`}
                        >
                          <Camera className="w-2.5 h-2.5" />
                          {photoCounts[d.id]}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-700">{d.deal_type || "Scope"}</td>
                    <td className="px-6 py-3"><StatusPill status={d.status} /></td>
                    <td className="px-6 py-3"><MiniPipeline deal={d} invoices={dealInvoicesMap[d.id] || []} testId={`mini-pipeline-${d.id}`} /></td>
                    <td className="px-6 py-3 text-zinc-600 text-xs">
                      <div>{d.lead_source}</div>
                      {d.lead_source === "Referral" && d.referral_source && (
                        <div className="text-[10px] text-zinc-400 mt-0.5">via {d.referral_source}</div>
                      )}
                    </td>
                    <td className="px-6 py-3 text-zinc-600 text-xs">{d.project_type}</td>
                    <td className="px-6 py-3 text-zinc-600 text-xs">{d.current_roof_type} → <span className="text-blue-700 font-bold">{d.proposed_roof_type}</span></td>
                    <td className="px-6 py-3 font-mono text-right">
                      {d.chosen_amount > 0 ? (
                        formatCurrency(d.chosen_amount)
                      ) : estValue > 0 ? (
                        <span className="text-zinc-400" title="Estimated — middle proposal option (counted in Pipeline Revenue). Becomes firm when a tier is chosen." data-testid={`deal-est-value-${d.id}`}>
                          {formatCurrency(estValue)} <span className="text-[9px] font-bold uppercase">est.</span>
                        </span>
                      ) : (
                        formatCurrency(0)
                      )}
                    </td>
                    <td className="px-6 py-3 font-mono text-right font-bold">
                      {showEstProfit ? (
                        <span
                          className="text-emerald-600/70"
                          title="Estimated profit — 33% of deal value (13% overhead + 20% profit). Becomes actual once costs are recorded."
                          data-testid={`deal-est-profit-${d.id}`}
                        >
                          {formatCurrency(estProfit)} <span className="text-[9px] font-bold uppercase">est.</span>
                        </span>
                      ) : (
                        <span className={profit >= 0 ? "text-emerald-700" : "text-red-700"}>{formatCurrency(profit)}</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-1">
                        <button data-testid={`edit-deal-${d.id}`} onClick={() => openEdit(d)} title="Edit" className="p-1.5 hover:bg-zinc-200 rounded-sm"><Pencil className="w-3.5 h-3.5" /></button>
                        {isOpen && (
                          <button data-testid={`archive-deal-${d.id}`} onClick={() => archive(d)} title="Move to Past Leads" className="p-1.5 hover:bg-zinc-200 rounded-sm text-zinc-600"><Archive className="w-3.5 h-3.5" /></button>
                        )}
                        <button data-testid={`delete-deal-${d.id}`} onClick={() => remove(d)} title="Delete" className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <Modal wide title={editing ? "Edit Project" : "New Project"} onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="space-y-5" data-testid="deal-form">
            <Grid2>
              <Field label="Project Title *">
                <Input data-testid="deal-title" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              </Field>
              <Field label="Type">
                <Select
                  data-testid="deal-type"
                  value={form.deal_type}
                  onChange={(v) => setForm({ ...form, deal_type: v })}
                  options={options.deal_types || ["Assessment", "Scope"]}
                />
              </Field>
            </Grid2>

            <Grid2>
              <Field label="Customer / Billed-To (e.g., Property Manager)">
                <Select data-testid="deal-customer-contact" value={form.customer_contact_id || ""} onChange={(v) => setForm({ ...form, customer_contact_id: v })} options={contactOpts} />
              </Field>
              <Field label="Property Owner (if different)">
                <Select data-testid="deal-owner-contact" value={form.owner_contact_id || ""} onChange={(v) => setForm({ ...form, owner_contact_id: v })} options={contactOpts} />
              </Field>
              <Field label="Primary Contact">
                <Select data-testid="deal-contact" value={form.contact_id || ""} onChange={(v) => setForm({ ...form, contact_id: v })} options={contactOpts} />
              </Field>
              <Field label="Property">
                <Select
                  data-testid="deal-property"
                  value={form.property_id || ""}
                  onChange={(v) => {
                    // Auto-hydrate Contact + Title from the selected property — only fill blanks.
                    const patch = { property_id: v };
                    if (v) {
                      const p = properties.find((x) => x.id === v);
                      if (p) {
                        if (!form.title && p.property_name) patch.title = p.property_name;
                        if (!form.contact_id && p.property_contact_id) patch.contact_id = p.property_contact_id;
                        if (!form.owner_contact_id && p.property_contact_id) patch.owner_contact_id = p.property_contact_id;
                      }
                    }
                    setForm({ ...form, ...patch });
                  }}
                  options={propertyOpts}
                />
              </Field>
            </Grid2>

            <div className="pt-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Work Type — what are we scoping?</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="deal-worktype-selector">
                {WORK_TYPES.map((wt) => {
                  const active = workCategoryOf(form.proposed_roof_type) === wt.key;
                  return (
                    <button
                      key={wt.key}
                      type="button"
                      data-testid={`deal-worktype-${wt.key}`}
                      onClick={() => { if (!active) setForm({ ...form, proposed_roof_type: wt.default }); }}
                      className={`text-left px-3 py-2.5 border-2 rounded-sm transition-colors ${active ? "border-blue-700 bg-blue-50" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
                    >
                      <div className={`text-xs font-black uppercase tracking-wide ${active ? "text-blue-800" : "text-zinc-800"}`}>{wt.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{wt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <Grid2>
              <Field label="Lead Source">
                <Select data-testid="deal-lead-source" value={form.lead_source} onChange={(v) => setForm({ ...form, lead_source: v, referral_source: v === "Referral" ? form.referral_source : "" })} options={options.lead_sources} />
              </Field>
              {form.lead_source === "Referral" && (
                <Field label="Referral Source (who?)">
                  <Input data-testid="deal-referral-source" value={form.referral_source} onChange={(v) => setForm({ ...form, referral_source: v })} placeholder="Name of referrer" />
                </Field>
              )}
              <Field label="Status">
                <Select data-testid="deal-status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={options.deal_statuses} />
              </Field>
              {form.status === "Lost" && (
                <>
                  <Field label="Lost Reason">
                    <Select data-testid="deal-lost-reason" value={form.lost_reason || ""} onChange={(v) => setForm({ ...form, lost_reason: v })} options={options.lost_reasons || ["Price", "Timing", "Chose competitor", "No response", "Project cancelled", "Other"]} />
                  </Field>
                  <Field label="Lost Detail (optional)">
                    <Input data-testid="deal-lost-detail" value={form.lost_reason_detail || ""} onChange={(v) => setForm({ ...form, lost_reason_detail: v })} placeholder="e.g. competitor was $8k under" />
                  </Field>
                </>
              )}
              <Field label="Project Type">
                <Select data-testid="deal-project-type" value={form.project_type} onChange={(v) => setForm({ ...form, project_type: v })} options={options.project_types} />
              </Field>
              <Field label="Date Sent">
                <Input data-testid="deal-date-sent" type="date" value={form.date_sent} onChange={(v) => setForm({ ...form, date_sent: v })} />
              </Field>
              <Field label="Current Roof Type / Or Construction Project">
                <Select data-testid="deal-current-roof" value={form.current_roof_type} onChange={(v) => setForm({ ...form, current_roof_type: v })} options={options.current_roof_types?.length ? options.current_roof_types : options.roof_types} />
              </Field>
              <Field label={workCategoryOf(form.proposed_roof_type) === "construction" ? "Construction Scope Type" : "Proposed Roof System"}>
                <Select data-testid="deal-proposed-roof" value={form.proposed_roof_type} onChange={(v) => setForm({ ...form, proposed_roof_type: v })} options={(options.roof_types || []).filter((rt) => workCategoryOf(rt) === workCategoryOf(form.proposed_roof_type))} />
                <ScopePreview currentRoof={form.current_roof_type} proposedRoof={form.proposed_roof_type} />
              </Field>
              <Field label={isAdmin ? "Assigned To" : "Assigned To (admin only)"}>
                <Select
                  data-testid="deal-assigned-to"
                  value={form.assigned_to_user_id || ""}
                  onChange={(v) => setForm({ ...form, assigned_to_user_id: v })}
                  options={userOpts}
                  disabled={!isAdmin}
                />
              </Field>
            </Grid2>

            {/* Construction-scope inputs — only when the proposed item is non-roofing (Construction Project / Other) */}
            {(/^(construction project|other)$/i.test(form.proposed_roof_type || "") || /other construction work/i.test(form.current_roof_type || "")) && (
              <div className="pt-4 border-t border-zinc-200 space-y-5" data-testid="custom-scope-block">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                    Construction / Remodel Scope · 2-Page PDF
                  </div>
                  <div className="text-[10px] text-zinc-500">
                    One bullet per line — printed on Page 1 of the proposal.
                  </div>
                </div>

                {/* Template bar — one-click reusable requirement/exclusion sets */}
                <div className="border border-zinc-300 bg-zinc-50 rounded-sm p-3 space-y-2" data-testid="construction-template-bar">
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      data-testid="construction-template-select"
                      value=""
                      onChange={(e) => {
                        const t = ctTemplates.find((x) => x.id === e.target.value);
                        if (t) applyCtTemplate(t);
                      }}
                      className="h-9 px-2 border border-zinc-300 rounded-sm text-xs bg-white focus:outline-none focus:border-blue-700 min-w-[220px]"
                    >
                      <option value="">Apply a scope template…</option>
                      {ctTemplates.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      data-testid="construction-template-save"
                      onClick={saveCtTemplate}
                      className="h-9 px-3 text-[10px] font-bold uppercase tracking-wider border border-blue-700 text-blue-700 bg-white hover:bg-blue-50 rounded-sm"
                    >
                      Save Current as Template
                    </button>
                    <div className="text-[10px] text-zinc-500">
                      Templates fill Requirements, Materials/Equipment &amp; Exclusions in one click.
                    </div>
                  </div>
                  {ctTemplates.length > 0 && (
                    <details className="text-[10px] text-zinc-500">
                      <summary className="cursor-pointer hover:text-zinc-800">Manage templates ({ctTemplates.length})</summary>
                      <div className="mt-1.5 space-y-1">
                        {ctTemplates.map((t) => (
                          <div key={t.id} className="flex items-center gap-2">
                            <span className="flex-1 font-semibold text-zinc-700">{t.name}</span>
                            <button
                              type="button"
                              data-testid={`construction-template-delete-${t.id}`}
                              onClick={() => deleteCtTemplate(t)}
                              className="text-zinc-400 hover:text-red-600"
                              title="Delete template"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>

                <Field label={`Project Type (overrides PDF label · auto from "${form.proposed_roof_type || "—"}")`}>
                  <Input
                    data-testid="deal-project-type-override"
                    value={form.project_type_override || ""}
                    onChange={(v) => setForm({ ...form, project_type_override: v })}
                    placeholder="e.g., Drainage & Grading, Concrete Walkway, Demolition"
                  />
                </Field>

                {/* ===== Scope of Work — Project Requirements + Other Requirements together ===== */}
                <div className="border-2 border-blue-200 rounded-sm p-4 bg-blue-50/30 space-y-4">
                  {/* Header band: "Scope of Work" on the left, subtitle input centered on the same row. */}
                  <div className="grid grid-cols-3 items-center gap-3 -mt-1">
                    <div className="text-[11px] font-black uppercase tracking-[0.18em] text-blue-800">
                      Scope of Work
                    </div>
                    <Input
                      data-testid="deal-construction-scope-subtitle"
                      value={form.construction_scope_subtitle || ""}
                      onChange={(v) => setForm({ ...form, construction_scope_subtitle: v })}
                      placeholder="Scope subtitle — e.g., Landscape Scope"
                    />
                    <div></div>
                  </div>

                  <Field label="Project Requirements">
                    <textarea
                      data-testid="deal-construction-project-requirements"
                      value={form.construction_project_requirements || ""}
                      onChange={(e) => setForm({ ...form, construction_project_requirements: e.target.value })}
                      rows={6}
                      placeholder={"Site preparation — clear necessary material, debris, excavate, and layout entire 330' long project section\nStructural fill placement and grading — Supply Clean Class 1 Structural Fill for stable compaction. Place and fill 5-8\" lifts for proper compaction and stability, grade and fill to create positive drainage away from both building foundations, taper the fill for proper runoff directing to the center of the trench, ensure proper tamping to create a structured stable runoff\nRiver rock surface layer — install 2-3\" of 1-3\" river rock evenly throughout the entire excavated area\nDownspout extensions and metal work — Install 110' +/- of downspout extensions and 45's"}
                      className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:border-blue-700 font-mono bg-white"
                    />
                    <div className="text-[10px] text-zinc-500 mt-1">The work being performed — one detailed bullet per line. Be specific; this is what the customer reads first.</div>
                  </Field>

                  <Field label="Other Requirements (Materials / Equipment / Metal)">
                    <textarea
                      data-testid="deal-construction-other-requirements"
                      value={form.construction_other_requirements || ""}
                      onChange={(e) => setForm({ ...form, construction_other_requirements: e.target.value })}
                      rows={4}
                      placeholder={"MATERIALS — 155 ton of class 1 clean structural fill, 70 ton of 1-3\" river rock\nEQUIPMENT — Skip Loader, 150 lb Plate Tamper\nMETAL — 110' of drain extensions and 45 degree elbows"}
                      className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:border-blue-700 font-mono bg-white"
                    />
                    <div className="text-[10px] text-zinc-500 mt-1">Quantities and key spec items — materials, equipment, metal counts.</div>
                  </Field>
                </div>

                {/* ===== Exclusions — separate, defaults pre-filled, rarely changes ===== */}
                <div className="border-2 border-amber-200 rounded-sm p-4 bg-amber-50/40 space-y-2">
                  <div className="flex items-center justify-between -mt-1">
                    <div className="text-[11px] font-black uppercase tracking-[0.18em] text-amber-800">
                      Exclusions · Standard Boilerplate
                    </div>
                    <button
                      type="button"
                      data-testid="deal-exclusions-reset"
                      onClick={() => setForm({ ...form, construction_exclusions: DEFAULT_CONSTRUCTION_EXCLUSIONS })}
                      className="text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 underline-offset-2 hover:underline"
                    >
                      Reset to defaults
                    </button>
                  </div>
                  <div className="text-[10px] text-amber-800/80">
                    These items are <strong>not</strong> covered by this project. Edit only when this specific job has unusual exclusions.
                  </div>
                  <textarea
                    data-testid="deal-construction-exclusions"
                    value={form.construction_exclusions || ""}
                    onChange={(e) => setForm({ ...form, construction_exclusions: e.target.value })}
                    rows={4}
                    placeholder={DEFAULT_CONSTRUCTION_EXCLUSIONS}
                    className="w-full px-3 py-2 border border-amber-300 rounded-sm text-sm focus:outline-none focus:border-amber-700 font-mono bg-white"
                  />
                </div>

                <details className="text-[10px] text-zinc-500">
                  <summary className="cursor-pointer hover:text-zinc-800">Legacy single-textarea scope (advanced / back-compat)</summary>
                  <textarea
                    data-testid="deal-custom-scope"
                    value={form.custom_scope || ""}
                    onChange={(e) => setForm({ ...form, custom_scope: e.target.value })}
                    rows={5}
                    placeholder={"Falls back to this when the buckets above are empty. All of this text will print under Project Requirements on the PDF."}
                    className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:border-blue-700 font-mono"
                  />
                </details>
              </div>
            )}

            <div className="pt-4 border-t border-zinc-200">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Measurements</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Field label="Property SqFt">
                  <Input data-testid="deal-property-sqft" type="number" min="0" step="1" value={form.property_sqft} onChange={(v) => setForm({ ...form, property_sqft: v })} />
                </Field>
                <Field label="Perimeter LnFt">
                  <Input data-testid="deal-perimeter-lnft" type="number" min="0" step="1" value={form.perimeter_lnft} onChange={(v) => setForm({ ...form, perimeter_lnft: v })} />
                </Field>
                <Field label="Avg Parapet Ht (ft)">
                  <Input data-testid="deal-parapet-height" type="number" min="0" step="0.5" value={form.avg_parapet_height} onChange={(v) => setForm({ ...form, avg_parapet_height: v })} />
                </Field>
                <Field label="Total SqFt (auto)">
                  <div className="h-10 flex items-center px-3 border border-zinc-200 bg-zinc-50 rounded-sm text-sm font-mono font-bold text-zinc-950" data-testid="deal-total-sqft">
                    {((parseFloat(form.property_sqft || 0)) + (parseFloat(form.perimeter_lnft || 0) * parseFloat(form.avg_parapet_height || 0))).toLocaleString()}
                  </div>
                </Field>
              </div>
              <div className="text-xs text-zinc-500 mt-2">Total = Property SqFt + (Perimeter LnFt × Avg Parapet Height)</div>
            </div>

            <div className="pt-4 border-t border-zinc-200">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Spec Sheet — Warranty Add-Ons &amp; Product</div>
                <button
                  type="button"
                  data-testid="calc-warranties-btn"
                  onClick={() => {
                    const sqft = (parseFloat(form.property_sqft || 0)) + (parseFloat(form.perimeter_lnft || 0) * parseFloat(form.avg_parapet_height || 0));
                    if (sqft <= 0) { toast.error("Enter Property SqFt / Perimeter / Parapet first so we know the SQ count."); return; }
                    const sq = sqft / 100;
                    const isFarmScope = /farm|fluid applied/i.test(form.proposed_roof_type || "");
                    const rider = 3.5 * sq;  // Hail Rider only on FARM 20/25-yr
                    const w10 = Math.max(9.0 * sq, 1250);
                    const w15 = Math.max(12.0 * sq, 1500);
                    const w20 = Math.max(15.0 * sq, 1750) + (isFarmScope ? rider : 0);
                    const w25 = Math.max(17.5 * sq, 2000) + rider;
                    // Round UP to the next $100 so the input fields (which use step=100)
                    // never carry decimals or odd $50 values like $123.50.
                    const roundUpTo100 = (n) => Math.ceil(n / 100) * 100;
                    const next = {
                      ...form,
                      warranty_20yr_add: roundUpTo100(w20),
                      warranty_15yr_add: roundUpTo100(w15),
                      warranty_10yr_add: roundUpTo100(w10),
                    };
                    if (isFarmScope) {
                      next.warranty_25yr_add = roundUpTo100(w25);
                    } else {
                      // Non-FARM scopes do not offer 25-yr — clear it so old values don't linger.
                      next.warranty_25yr_add = 0;
                    }
                    setForm(next);
                    toast.success(isFarmScope
                      ? `FARM warranties calculated for ${sq.toFixed(1)} SQ (incl. Hail Rider on 20/25-yr) — rounded up to nearest $100`
                      : `Warranties calculated for ${sq.toFixed(1)} SQ (25-yr skipped — FARM only) — rounded up to nearest $100`);
                  }}
                  className="inline-flex items-center gap-2 bg-white border border-blue-700 text-blue-700 px-3 h-8 text-[10px] font-bold uppercase tracking-wider hover:bg-blue-50 rounded-sm transition-colors"
                  title="Auto-fill warranty add-ons from Total SqFt using the standard per-square rates. 25-yr + Hail Rider are FARM-only."
                >
                  <Calculator className="w-3.5 h-3.5" /> Calculate Warranties
                </button>
              </div>
              <Field label="Product Description (override)">
                <Input data-testid="deal-product-desc" value={form.product_description} onChange={(v) => setForm({ ...form, product_description: v })} placeholder="e.g., Silicone Roof System w/Granules Over Single-Ply Investment" />
              </Field>
              {(() => {
                const isConstruction = /^(construction project|other)$/i.test(form.proposed_roof_type || "") || /other construction work/i.test(form.current_roof_type || "");
                if (isConstruction) {
                  return (
                    <div className="text-[11px] text-zinc-500 mt-3 italic" data-testid="construction-no-warranty">
                      Construction Project — no manufacturer warranty tiers. Skip the warranty add-on rows.
                    </div>
                  );
                }
                const isFarm = /farm|fluid applied/i.test(form.proposed_roof_type || "");
                return (
                  <>
                    <div className={`grid grid-cols-2 sm:grid-cols-3 gap-4 mt-3 ${isFarm ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
                      {isFarm && (
                        <Field label="25-Yr Warranty Add ($)" hint="FARM only">
                          <Input data-testid="deal-w25" type="number" min="0" step="100" value={form.warranty_25yr_add} onChange={(v) => setForm({ ...form, warranty_25yr_add: v })} />
                        </Field>
                      )}
                      <Field label="20-Yr Warranty Add ($)">
                        <Input data-testid="deal-w20" type="number" min="0" step="100" value={form.warranty_20yr_add} onChange={(v) => setForm({ ...form, warranty_20yr_add: v })} />
                      </Field>
                      <Field label="15-Yr Warranty Add ($)">
                        <Input data-testid="deal-w15" type="number" min="0" step="100" value={form.warranty_15yr_add} onChange={(v) => setForm({ ...form, warranty_15yr_add: v })} />
                      </Field>
                      <Field label="10-Yr Warranty Add ($)">
                        <Input data-testid="deal-w10" type="number" min="0" step="100" value={form.warranty_10yr_add} onChange={(v) => setForm({ ...form, warranty_10yr_add: v })} />
                      </Field>
                      <Field label="Coating Color">
                        <Input data-testid="deal-color" value={form.warranty_color} onChange={(v) => setForm({ ...form, warranty_color: v })} placeholder="white" />
                      </Field>
                    </div>
                    <div className="text-xs text-zinc-500 mt-2">
                      <b>Standard rates</b> (per SQ, $-minimum):&nbsp;
                      <span className="font-mono">10-Yr&nbsp;$9.00/$1,250</span> &middot;
                      <span className="font-mono">15-Yr&nbsp;$12.00/$1,500</span> &middot;
                      <span className="font-mono">20-Yr&nbsp;$15.00/$1,750</span>.&nbsp;
                      <b>Hail Rider</b> <span className="font-mono">$3.50/SQ</span> + 25-Yr (<span className="font-mono">$17.50/$2,000</span>) are <b>FARM only</b>.&nbsp;
                      Option B→20-yr · Option C→15-yr · Option D→10-yr (Option A→25-yr appears only when Proposed Roof Type is FARM).
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="pt-4 border-t border-zinc-200">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">
                {(() => {
                  const isConstruction = /^(construction project|other)$/i.test(form.proposed_roof_type || "") || /other construction work/i.test(form.current_roof_type || "");
                  if (isConstruction) return "Project Pricing — Single Price";
                  return form.deal_type === "Assessment" ? "Assessment — 3 Roof System Options" : "Scope — Pricing Options";
                })()}
              </div>
              {(() => {
                const isConstruction = /^(construction project|other)$/i.test(form.proposed_roof_type || "") || /other construction work/i.test(form.current_roof_type || "");
                if (isConstruction) {
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Project Price ($)">
                        <Input data-testid="deal-construction-price" type="number" min="0" step="0.01" value={form.proposal_option_1} onChange={(v) => setForm({ ...form, proposal_option_1: v, proposal_option_2: 0, proposal_option_3: 0, proposal_option_25yr: 0 })} placeholder="Single project total" />
                      </Field>
                      <div className="text-[11px] text-zinc-500 mt-7 italic">No 25/20/15/10-yr tiers on Construction Projects — proposal PDF will print one price.</div>
                    </div>
                  );
                }
                const isFarm = /farm|fluid applied/i.test(form.proposed_roof_type || "");
                const isShingle = /shingle/i.test(form.proposed_roof_type || "");
                return (
                  <>
                  <div className={`grid gap-4 ${isFarm ? "grid-cols-2 md:grid-cols-4" : "grid-cols-1 md:grid-cols-3"}`}>
                    {isFarm && (
                      <Field label="Option A ($) — 25-yr">
                        <Input data-testid="deal-option-25yr" type="number" min="0" step="0.01" value={form.proposal_option_25yr} onChange={(v) => setForm({ ...form, proposal_option_25yr: v })} />
                      </Field>
                    )}
                    <Field label={isShingle ? "Option B ($) — Designer / Luxury" : "Option B ($) — 20-yr"}>
                      <Input data-testid="deal-option-1" type="number" min="0" step="0.01" value={form.proposal_option_1} onChange={(v) => setForm({ ...form, proposal_option_1: v })} />
                    </Field>
                    <Field label={isShingle ? "Option C ($) — Architectural" : "Option C ($) — 15-yr"}>
                      <Input data-testid="deal-option-2" type="number" min="0" step="0.01" value={form.proposal_option_2} onChange={(v) => setForm({ ...form, proposal_option_2: v })} />
                    </Field>
                    <Field label={isShingle ? "Option D ($) — 3-Tab" : "Option D ($) — 10-yr"}>
                      <Input data-testid="deal-option-3" type="number" min="0" step="0.01" value={form.proposal_option_3} onChange={(v) => setForm({ ...form, proposal_option_3: v })} />
                    </Field>
                  </div>
                  {isShingle && (
                    <div className="mt-3">
                      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Shingle Product Lines — print on the proposal (optional)</div>
                      <datalist id="shingle-products-designer">{SHINGLE_PRODUCTS.designer.map((p) => <option key={p} value={p} />)}</datalist>
                      <datalist id="shingle-products-architectural">{SHINGLE_PRODUCTS.architectural.map((p) => <option key={p} value={p} />)}</datalist>
                      <datalist id="shingle-products-three-tab">{SHINGLE_PRODUCTS.three_tab.map((p) => <option key={p} value={p} />)}</datalist>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Designer / Luxury product">
                          <Input data-testid="deal-shingle-product-1" list="shingle-products-designer" value={form.proposal_option_1_label || ""} onChange={(v) => setForm({ ...form, proposal_option_1_label: v })} placeholder="e.g., GAF Grand Sequoia" />
                        </Field>
                        <Field label="Architectural product">
                          <Input data-testid="deal-shingle-product-2" list="shingle-products-architectural" value={form.proposal_option_2_label || ""} onChange={(v) => setForm({ ...form, proposal_option_2_label: v })} placeholder="e.g., GAF Timberline HDZ" />
                        </Field>
                        <Field label="3-Tab product">
                          <Input data-testid="deal-shingle-product-3" list="shingle-products-three-tab" value={form.proposal_option_3_label || ""} onChange={(v) => setForm({ ...form, proposal_option_3_label: v })} placeholder="e.g., GAF Royal Sovereign" />
                        </Field>
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-1.5">Name a real product and the proposal prints it (e.g., "GAF Timberline HDZ") instead of the generic grade. Leave blank to print the grade name.</div>
                    </div>
                  )}
                  </>
                );
              })()}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Chosen Amount ($)">
                  <Input data-testid="deal-chosen-amount" type="number" min="0" step="0.01" value={form.chosen_amount} onChange={(v) => setForm({ ...form, chosen_amount: v })} />
                </Field>
                <Field label="Chosen Date">
                  <Input data-testid="deal-chosen-date" type="date" value={form.chosen_date} onChange={(v) => setForm({ ...form, chosen_date: v })} />
                </Field>
              </div>
              <div className="text-xs text-zinc-500 mt-2">
                Tip: After saving, open the project to add payment milestones and vendor cost line items.
              </div>
            </div>

            {/* Schedule — drives the Project Calendar */}
            <div className="border-t border-zinc-200 pt-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Schedule (Project Calendar)</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Scheduled Start">
                  <Input data-testid="deal-scheduled-start" type="date" value={form.scheduled_start_date} onChange={(v) => setForm({ ...form, scheduled_start_date: v })} />
                </Field>
                <Field label="Scheduled End">
                  <Input data-testid="deal-scheduled-end" type="date" value={form.scheduled_end_date} onChange={(v) => setForm({ ...form, scheduled_end_date: v })} />
                </Field>
                <Field label="Material Order Date">
                  <Input data-testid="deal-material-order" type="date" value={form.material_order_date} onChange={(v) => setForm({ ...form, material_order_date: v })} />
                </Field>
              </div>
              <div className="text-[11px] text-zinc-500 mt-2">
                These dates appear on the Project Calendar — drag events to reschedule.
              </div>
            </div>

            <Field label="Notes">
              <div className="flex justify-end mb-1">
                <GrammarCheck text={form.notes || ""} onChange={(v) => setForm({ ...form, notes: v })} label="Check Grammar" />
              </div>
              <textarea
                data-testid="deal-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-zinc-300 bg-white rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
              />
            </Field>

            <div className="flex justify-end gap-2 pt-4 border-t border-zinc-200">
              <button type="button" onClick={() => setOpen(false)} className="px-4 h-10 text-xs font-bold uppercase tracking-wider border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
              <button type="submit" disabled={loading} data-testid="deal-save" className="px-4 h-10 text-xs font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 rounded-sm disabled:opacity-50">{loading ? "Saving..." : "Save Project"}</button>
            </div>
          </form>
        </Modal>
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        title="Delete Project?"
        message={`This will permanently delete ${confirmTarget?.title || "this project"} and all associated milestones, cost items, and documents. This action cannot be undone.`}
        onConfirm={removeConfirmed}
        onClose={() => setConfirmTarget(null)}
      />
      <ConfirmDialog
        open={!!archiveTarget}
        danger={false}
        title="Move to Past Leads?"
        confirmLabel="Move"
        message={`Move "${archiveTarget?.title || "this lead"}" to the Past Lead Prospects archive? You can find it later via the Past Leads filter.`}
        onConfirm={archiveConfirmed}
        onClose={() => setArchiveTarget(null)}
      />
    </div>
  );
}
